import type { Login, SignUpWithSubscription } from '~~/layers/base/types/auth'

export function useAuthActions() {
    // Pinia stores
    const customerAuthStore = useCustomerAuthAccsStore()
    const customerStore = useCustomerStore() // Store for customer data (profile, id, etc.)
    const designProjectStore = useDesignProjectStore() // Store for design project actions (e.g., assigning user)

    // Extract reactive references from the stores
    const { customer } = storeToRefs(customerStore) // Reactive reference to current customer (Customer | null)
    const { inProgress } = storeToRefs(customerAuthStore) // Reactive flag indicating if an auth action is in progress

    /**
     * Login flow
     * @param payload - Login credentials (email/password, etc.)
     *
     * Steps:
     * 1. Set inProgress to true to indicate login is ongoing.
     * 2. Call the login action from customerAuthStore with the payload.
     * 3. If login succeeds and customer has an ID, assign the user to a design project.
     * 4. Finally, reset inProgress to false to indicate login is complete.
     */
    const loginAndAssignDesignProject = async (payload: Login) => {
        // Call the login API/action
        await customerAuthStore.login(payload)

        try {
            inProgress.value = true
            // TODO(better-auth): this currently assigns design projects using the Magento/ACCS
            // customer id. Once we have a canonical app-level user id (linked to Better Auth),
            // use that identifier instead of `customer.value.id.toString()`.
            // If customer exists after login, assign them to a design project
            if (customer?.value?.id) {
                await designProjectStore.assignUserToDesignProject(customer.value.id.toString())
            }
        } finally {
            // Ensure inProgress is reset even if an error occurs
            inProgress.value = false
        }
    }

    /**
     * Sign-up flow with subscription
     * @param payload - Sign-up details, possibly including subscription info
     *
     * Steps:
     * 1. Set inProgress to true to indicate signup is ongoing.
     * 2. Call the signUp action from customerAuthStore with the payload.
     * 3. If sign-up succeeds and customer has an ID, assign the user to a design project.
     * 4. Finally, reset inProgress to false to indicate signup is complete.
     */
    const signUpAndAssignDesignProject = async (payload: SignUpWithSubscription) => {
        // Call the sign-up API/action
        await customerAuthStore.signUp(payload)

        try {
            inProgress.value = true
            // TODO(better-auth): this currently assigns design projects using the Magento/ACCS
            // customer id. Once we have a canonical app-level user id (linked to Better Auth),
            // use that identifier instead of `customer.value.id.toString()`.
            // If customer exists after sign-up, assign them to a design project
            if (customer?.value?.id) {
                await designProjectStore.assignUserToDesignProject(customer.value.id.toString())
            }
        } finally {
            // Ensure inProgress is reset even if an error occurs
            inProgress.value = false
        }
    }

    // Expose the login and sign-up flows for components to use
    return {
        loginAndAssignDesignProject,
        signUpAndAssignDesignProject,
    }
}
